import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Navbar from "components/navbar";
import { HStack, IconButton, Button } from "@chakra-ui/react";
import { AiFillBackward, AiFillForward } from "react-icons/ai";
import { fetchCategories, deleteCategoryApi } from "../../../services/customAPI";
import { DTO, Types } from "shared-lib";
import { errorToast, successToast } from "../../../utils/commons";
import ConfirmationDialog from "../../../components/common/ConfirmationDialog";
import Loader from "components/loader/loader";

const CategoriesPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [categories, setCategories] = useState<DTO.CategoryDTO[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<number | null>(null);
  const itemsPerPage = 6;
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = Math.ceil(categories.length / itemsPerPage);

  const getCategories = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetchCategories();
      setCategories(response.data ?? []);
    } catch (error) {
      errorToast(t("ErrorFetchingCategories"));
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    getCategories();
  }, [getCategories]);

  const deleteCategory = async (id: number) => {
    try {
      await deleteCategoryApi(id);
      successToast(t("CategoryDeletedSuccessfully"));
      getCategories();
    } catch (error) {
      errorToast(t("ErrorDeletingCategory"));
    }
  };

  const handleDeleteClick = (categoryId: number) => {
    setCategoryToDelete(categoryId);
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = () => {
    if (categoryToDelete) {
      deleteCategory(categoryToDelete);
    }
    setShowDeleteDialog(false);
    setCategoryToDelete(null);
  };

  const handleCancelDelete = () => {
    setShowDeleteDialog(false);
    setCategoryToDelete(null);
  };

  const addCategory = () => {
    navigate("/admin/settings/categories/create");
  };

  const startIdx = (currentPage - 1) * itemsPerPage;
  const currentItems = categories.slice(startIdx, startIdx + itemsPerPage);

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <Navbar flag={false} brandText={t("Categories")} />

      {isLoading ? (
        <Loader />
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 mb-6">
            {currentItems.map((cat) => {
              return (
                <div
                  key={cat.id}
                  className="bg-white/5 backdrop-blur-sm shadow-lg rounded-2xl overflow-hidden border border-white/10 hover:border-white/20 transition-all duration-200"
                >
                  {/* Image with overlay badges */}
                  <div className="relative">
                    <img
                      src={`${process.env.REACT_APP_BASE_URL}/uploads/${cat.urlImg}`}
                      alt={cat.name}
                      className="w-full h-48 object-cover"
                    />
                    {/* Transport mode badge — top left */}
                    {cat.transportMode && (
                      <span
                        className={`absolute top-3 left-3 text-[11px] font-bold px-2.5 py-1 rounded-full backdrop-blur-md ${cat.transportMode === Types.TransportMode.CARGO
                          ? "bg-amber-500/80 text-white"
                          : "bg-cyan-500/80 text-white"
                          }`}
                      >
                        {Types.getTransportModeEmoji(cat.transportMode)} {Types.getTransportModeLabel(cat.transportMode)}
                      </span>
                    )}
                    {/* Vehicle type badge — top right */}
                    {cat.vehicleTypeName && (
                      <span className="absolute top-3 right-3 text-[11px] font-bold px-2.5 py-1 rounded-full bg-black/60 text-white backdrop-blur-md">
                        {cat.vehicleTypeName}
                      </span>
                    )}
                  </div>
                  <div className="p-4">
                    <h2 className="text-xl font-semibold mb-1 text-white">
                      {cat.name}
                    </h2>
                    <p className="text-gray-400">
                      {t("Multiplicador")} {cat.multiply}
                    </p>
                    <span
                      className={`inline-block text-xs font-semibold px-2.5 py-0.5 rounded-full mt-2 ${cat.active
                        ? "bg-green-500/20 text-green-400"
                        : "bg-red-500/20 text-red-400"
                        }`}
                    >
                      {cat.active ? t("Ativo") : t("Inativo")}
                    </span>

                    <div className="mt-4 flex gap-2">
                      <button
                        className="flex-1 rounded-lg border border-gray-600 px-3 py-2 text-sm font-medium text-gray-300 hover:border-white/30 hover:text-white transition-colors"
                        onClick={() =>
                          navigate(
                            `/admin/settings/categories/create/${cat.id}`
                          )
                        }
                      >
                        {t("Edit")}
                      </button>
                      <button
                        onClick={() => handleDeleteClick(cat.id)}
                        className="flex-1 rounded-lg bg-red-600 px-3 py-2 text-sm font-semibold text-white hover:bg-red-700 transition-colors"
                      >
                        {t("delete")}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Empty state */}
          {!categories.length && (
            <div className="text-center py-10">
              <p className="text-gray-500 dark:text-gray-400 mb-4">
                {t("NoConfigurationsFound")}
              </p>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <HStack justify="center" mb={4}>
              <IconButton
                icon={<AiFillBackward />}
                aria-label="Previous"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                isDisabled={currentPage === 1}
              />
              {Array.from({ length: totalPages }, (_, i) => (
                <Button
                  key={i + 1}
                  size="sm"
                  variant={currentPage === i + 1 ? "solid" : "outline"}
                  onClick={() => setCurrentPage(i + 1)}
                >
                  {i + 1}
                </Button>
              ))}
              <IconButton
                icon={<AiFillForward />}
                aria-label="Next"
                onClick={() =>
                  setCurrentPage((p) => Math.min(totalPages, p + 1))
                }
                isDisabled={currentPage === totalPages}
              />
            </HStack>
          )}

          {/* Add category button */}
          <div className="flex justify-end items-center mb-6">
            <button
              onClick={addCategory}
              className="rounded-lg bg-cyan-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-cyan-700 transition-colors"
            >
              {t("AddCategory")}
            </button>
          </div>
        </>
      )}

      <ConfirmationDialog
        isOpen={showDeleteDialog}
        title={t("ConfirmDeletion")}
        message={t("AreYouSureYouWantToDelete?")}
        confirmText={t("Delete")}
        cancelText={t("Cancel")}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </div>
  );
};

export default CategoriesPage;
